import { useState, useEffect, useCallback } from "react";
import { invoke, SubscriptionResult as Subscription } from "../lib/bridge";
import { CurrentPlanSection, PlanOptionsSection, formatDate } from "./CurrentPlanSection";
import { PromoCodeSection } from "./PromoCodeSection";
import { ConfirmDialog } from "./ui/ConfirmDialog";
import { Spinner } from "./ui/Spinner";

const PAYMENT_PENDING = "Payment received - your plan will update momentarily.";

const money = (n: number) => `$${n.toFixed(2)}`;

// Full (completed) months between now and the period end - the unit our
// prorated refund policy pays out on.
function fullMonthsRemaining(endIso: string | null): number {
  if (!endIso) return 0;
  const end = new Date(endIso);
  const now = new Date();
  if (end <= now) return 0;
  let months = (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());
  if (end.getDate() < now.getDate()) months -= 1;
  return Math.max(0, months);
}

// Refund eligibility per the Terms of Service. An immediate cancel earns a
// refund only within the first 7 days (full) or with >3 months left (prorated);
// otherwise the plan is non-refundable and the user should let it lapse.
function refundPolicy(sub: Subscription) {
  // Anchor the 7-day window to the original purchase so it doesn't reset on each
  // annual renewal. Fall back to the period start for rows synced before the
  // original-purchase date was tracked.
  const purchasedIso = sub.originalPurchasedAt ?? sub.currentPeriodStart;
  const purchased = purchasedIso ? new Date(purchasedIso) : null;
  const withinFirstWeek = purchased ? Date.now() - purchased.getTime() <= 7 * 24 * 60 * 60 * 1000 : false;
  const monthsLeft = fullMonthsRemaining(sub.currentPeriodEnd);
  return { withinFirstWeek, monthsLeft, refundableCancel: withinFirstWeek || monthsLeft > 3 };
}

export function AccountView({ onBack, justReturnedFromCheckout }: { onBack: () => void; justReturnedFromCheckout?: "success" | "cancel" | null }) {
  const [sub, setSub] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(
    justReturnedFromCheckout === "success"
      ? PAYMENT_PENDING
      : justReturnedFromCheckout === "cancel"
        ? "Checkout canceled."
        : "",
  );
  const [promo, setPromo] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState<{ message: string; onConfirm: () => void } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await invoke<Subscription>("get_subscription");
      setSub(s);
      return s;
    } catch (e) {
      setError(String(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Stripe activation is confirmed by webhook; after a successful checkout the
  // row may lag a moment, so poll a few times. Drop the "updating momentarily"
  // notice once the plan activates (or once we stop polling) so it shows once.
  const POLL_INTERVAL_MS = 2000;
  const POLL_MAX_ATTEMPTS = 5;
  const WEBHOOK_LAG_NOTICE = "Your plan update is taking a moment. Refresh the page if it doesn't appear shortly.";

  useEffect(() => {
    if (justReturnedFromCheckout !== "success") return;
    let n = 0;
    const id = setInterval(async () => {
      n += 1;
      const s = await refresh();
      const activated = s?.source === "stripe" && s.status !== "canceled" && s.tier !== "free";
      if (activated) {
        clearInterval(id);
        setNotice((cur) => (cur === PAYMENT_PENDING ? "" : cur));
      } else if (n >= POLL_MAX_ATTEMPTS) {
        clearInterval(id);
        setNotice(WEBHOOK_LAG_NOTICE);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [justReturnedFromCheckout, refresh]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  const run = async <T,>(fn: () => Promise<T>, after?: (r: T) => void) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await fn();
      if (after) after(r);
      else await refresh();
    } catch (e) {
      setError(String(e).replace(/^Error:\s*/, ""));
    } finally {
      setBusy(false);
    }
  };

  const startCheckout = (tier: "duo" | "family") =>
    run(
      () => invoke<string>("get_checkout_url", { tier }),
      (url: string) => {
        window.location.href = url;
      },
    );

  const switchTier = (tier: "duo" | "family") =>
    run(() => invoke<Subscription>("change_tier", { tier }), (s: Subscription) => setSub(s));

  const toggleAutoRenew = () =>
    run(() => invoke<Subscription>("set_auto_renew", { autoRenew: !sub!.autoRenew }), (s: Subscription) => setSub(s));

  // Cancel routing follows the refund policy in the Terms of Service:
  //   - within 7 days of purchase  => full refund, cancel immediately
  //   - >3 full months remaining   => prorated refund, cancel immediately
  //   - <=3 full months remaining  => non-refundable; turn off auto-renew instead
  //     of cancelling (an immediate cancel would forfeit the paid time for nothing)
  const cancelSub = () => {
    const tier = sub!.tier as "duo" | "family";
    const { withinFirstWeek, monthsLeft } = refundPolicy(sub!);
    const monthlyRate = sub!.prices[tier] / 12;
    const endDate = formatDate(sub!.currentPeriodEnd);
    // Free plans keep only the creator, so dropping to Free removes everyone else.
    const bootNow = sub!.memberCount > 1 ? " Everyone except the family creator will be removed from the family." : "";
    const bootAtEnd = sub!.memberCount > 1 ? " When it ends, everyone except the family creator will be removed from the family." : "";

    if (withinFirstWeek) {
      setCancelConfirm({
        message: `Cancel now and get a full refund? You're within 7 days of your purchase, so you'll be refunded ${money(sub!.prices[tier])} in full (processed manually within a few days). Your plan drops to Free right away.${bootNow}`,
        onConfirm: () => run(() => invoke<Subscription>("cancel_subscription"), (s: Subscription) => setSub(s)),
      });
      return;
    }

    if (monthsLeft > 3) {
      const refund = monthsLeft * monthlyRate;
      setCancelConfirm({
        message: `Cancel now and get a partial refund? You have ${monthsLeft} full months left, so you'll be refunded ${monthsLeft} x ${money(monthlyRate)} = ${money(refund)} (processed manually within a few days). Your plan drops to Free right away.${bootNow}`,
        onConfirm: () => run(() => invoke<Subscription>("cancel_subscription"), (s: Subscription) => setSub(s)),
      });
      return;
    }

    // <=3 months left: non-refundable - turn off auto-renew so the plan lapses
    // at term end rather than cancelling now for nothing. (The button only
    // routes here while auto-renew is still on.)
    setCancelConfirm({
      message: `With 3 months or fewer left, your subscription is non-refundable. Instead, we'll turn off auto-renewal: your plan stays active until ${endDate}, then drops to Free and won't be charged again.${bootAtEnd}`,
      onConfirm: () => run(() => invoke<Subscription>("set_auto_renew", { autoRenew: false }), (s: Subscription) => setSub(s)),
    });
  };

  const redeemPromo = () => {
    if (!promo.trim()) return;
    run(
      () => invoke<Subscription>("redeem_promo_code", { code: promo.trim() }),
      (s: Subscription) => {
        setSub(s);
        setPromo("");
        setNotice(s.message ?? "Promo code applied!");
      },
    );
  };

  if (loading) {
    return (
      <div className="container">
        <div className="header-row">
          <h1 className="header-title">Account</h1>
          <button onClick={onBack} className="btn-close">&times;</button>
        </div>
        <Spinner />
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="container">
        <div className="header-row">
          <h1 className="header-title">Account</h1>
          <button onClick={onBack} className="btn-close">&times;</button>
        </div>
        <p className="account-error">{error || "Couldn't load your account."}</p>
      </div>
    );
  }

  const isStripe = sub.source === "stripe" && sub.status !== "canceled" && sub.tier !== "free";
  const isPromo = sub.source === "promo" && sub.tier !== "free";
  const { refundableCancel } = refundPolicy(sub);
  // Show the cancel control when an immediate (refundable) cancel is available,
  // or when there's still auto-renew to turn off. With <=3 months left and
  // auto-renew already off the plan is simply lapsing - nothing to do.
  const showCancel = isStripe && (refundableCancel || sub.autoRenew);
  const cancelLabel = refundableCancel ? "Cancel subscription" : "Turn off auto-renewal";
  const periodLabel =
    sub.tier === "free"
      ? null
      : isPromo
        ? `Free until ${formatDate(sub.currentPeriodEnd)}`
        : sub.autoRenew
          ? `Renews on ${formatDate(sub.currentPeriodEnd)}`
          : `Ends on ${formatDate(sub.currentPeriodEnd)}`;

  return (
    <div className="container">
      <div className="header-row">
        <h1 className="header-title">Account</h1>
        <button onClick={onBack} className="btn-close">&times;</button>
      </div>

      {notice && <p className="account-notice">{notice}</p>}
      {error && <p className="account-error">{error}</p>}

      <CurrentPlanSection
        sub={sub}
        isStripe={isStripe}
        isPromo={isPromo}
        periodLabel={periodLabel}
        busy={busy}
        onToggleAutoRenew={toggleAutoRenew}
      />

      <PlanOptionsSection
        sub={sub}
        isStripe={isStripe}
        isPromo={isPromo}
        busy={busy}
        onStartCheckout={startCheckout}
        onSwitchTier={switchTier}
      />

      {showCancel && (
        <button className="btn-secondary btn-full account-cancel-btn" disabled={busy} onClick={cancelSub}>
          {cancelLabel}
        </button>
      )}

      {sub.tier === "free" && (
        <PromoCodeSection
          promo={promo}
          busy={busy}
          onPromoChange={setPromo}
          onRedeem={redeemPromo}
        />
      )}

      <ConfirmDialog
        open={!!cancelConfirm}
        message={cancelConfirm?.message ?? ""}
        confirmLabel="Confirm"
        cancelLabel="Keep subscription"
        variant="danger"
        onConfirm={() => { cancelConfirm?.onConfirm(); setCancelConfirm(null); }}
        onCancel={() => setCancelConfirm(null)}
      />
    </div>
  );
}
